/**
 * AdSense ad-unit slot IDs, keyed by the role the slot plays on the page.
 *
 * Why this file exists: nine distinct slot IDs used to be hardcoded across twelve components,
 * and none of them resolved to a unit that still existed in the account (`pub-7820192138716446`)
 * -- not even among archived units. That went unnoticed because `MotiveComponent.render()`
 * returned an empty `<span>`, so no slot ever rendered and no blank ever showed. Once the
 * component renders again, a wrong ID is a silently empty box, so the IDs live in one place
 * where they can be checked against the account.
 *
 * Mapped by role rather than one unit per placement: AdSense reporting should answer "does the
 * below-tool slot earn?", not fragment across twenty near-identical rows.
 *
 * See docs/proposed/AD_PLACEMENT_DESIGN.md 3.
 */
export const AdSlots = {
    /** Directly beneath a converter's tool, where the result appears. */
    belowTool: '2097852120',
    /** Top of a page, above the main content. */
    pageTop: '9666235439',
    /** Inside lists and mid-content, between blocks the user is already reading. */
    inContent: '3219362107',
    /** End of a page, above the footer. */
    aboveFooter: '7040072094',
    /** Desktop side rails. Both rails share this unit -- they are one product decision. */
    desktopRail: '1906280432',
} as const;

/**
 * The publisher, defined once. Everything that names one reads it from here: the `<ins>`
 * elements and the adsbygoogle library `ScriptLoadService` loads. They have to agree, or the
 * slots do not fill.
 *
 * `ca-pub-7820192138716446` is the account (confirmed in AdSense account settings), the only
 * publisher in the site's `ads.txt`, and the owner of the slot IDs above.
 *
 * There used to be a second definition. The SSR environment shipped `gadId:
 * 'ca-pub-5479498347583905'` in prod, dev and the base config, and `motive.component.tsx` read
 * it straight into `data-ad-client` -- so every `<ins>` named a publisher the loaded library did
 * not know and `ads.txt` did not authorise. That was invisible for as long as the manual ad path
 * rendered an empty `<span>`; the moment it rendered, it would have been unfilled slots and
 * unauthorised inventory. The field is now deleted from all three SSR environments and from the
 * front end's own `defaultEnvironment`, so this constant is the only publisher ID in the
 * codebase and there is nothing left to drift from.
 */
export const AD_CLIENT = 'ca-pub-7820192138716446';
